import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchService } from '../services/searchService';
import { useGrowingAreas } from './useGrowingAreas';
import { useBatches } from './useBatches';
import { useTasks } from './useTasks';
import { useActiveDevice } from './useActuators';

export interface UnifiedSearchResult {
  id: string;
  type: 'species' | 'pest' | 'disease' | 'area' | 'batch' | 'task' | 'action' | 'nav';
  title: string;
  subtitle?: string;
  url?: string;
  metadata?: any;
  similarity?: number;
}

export const useSearch = (query: string) => {
  const { activeDeviceId } = useActiveDevice();
  const { areas } = useGrowingAreas(activeDeviceId || undefined);
  const { batches } = useBatches();
  const { data: tasks } = useTasks();

  const trimmedQuery = query.trim().toLowerCase();

  // remote semantic search
  const { data: remoteResults, isLoading: isRemoteLoading } = useQuery({
    queryKey: ['search', trimmedQuery],
    queryFn: () => searchService.searchAll(trimmedQuery),
    enabled: trimmedQuery.length >= 2,
    staleTime: 60000,
  });

  // Local filtering & aggregation
  const results = useMemo(() => {
    if (trimmedQuery.length < 2) return [];

    const unified: UnifiedSearchResult[] = [];

    // 1. Add remote results
    if (remoteResults) {
      remoteResults.species.forEach((s) =>
        unified.push({
          id: s.entity_id,
          type: 'species',
          title: s.metadata.name || s.content,
          subtitle: 'Plant-O-Pedia',
          url: `/plant-o-pedia?id=${s.entity_id}`,
          similarity: s.similarity,
        }),
      );
      remoteResults.pests.forEach((p) =>
        unified.push({
          id: p.entity_id,
          type: 'pest',
          title: p.metadata.name_ui || p.content,
          subtitle: 'Škůdce',
          url: `/plant-o-pedia?type=pests&id=${p.entity_id}`,
          similarity: p.similarity,
        }),
      );
      remoteResults.diseases.forEach((d) =>
        unified.push({
          id: d.entity_id,
          type: 'disease',
          title: d.metadata.name_ui || d.content,
          subtitle: 'Choroba',
          url: `/plant-o-pedia?type=diseases&id=${d.entity_id}`,
          similarity: d.similarity,
        }),
      );
    }

    // 2. Local Areas
    areas
      .filter((a) => a.name.toLowerCase().includes(trimmedQuery))
      .forEach((a) =>
        unified.push({
          id: a.id,
          type: 'area',
          title: a.name,
          subtitle: `Záhon • ${a.location === 'outdoor' ? 'Venku' : 'Doma'}`,
          url: `/growing-areas/${a.id}`,
        }),
      );

    // 3. Local Batches
    batches
      .filter(
        (b) =>
          b.name.toLowerCase().includes(trimmedQuery) ||
          b.species_name?.toLowerCase().includes(trimmedQuery),
      )
      .forEach((b) =>
        unified.push({
          id: b.id,
          type: 'batch',
          title: b.name,
          subtitle: `Výsadba • ${b.species_name || 'Neznámá rostlina'}`,
          url: `/growth/batches/${b.id}`,
        }),
      );

    // 4. Local Tasks
    tasks
      ?.filter((t) => t.title.toLowerCase().includes(trimmedQuery))
      .forEach((t) =>
        unified.push({
          id: t.id,
          type: 'task',
          title: t.title,
          subtitle: 'Úkol',
          url: `/tasks?id=${t.id}`,
        }),
      );

    // 5. Actions & Nav (Static)
    const staticActions: UnifiedSearchResult[] = [
      {
        id: 'nav-dash',
        type: 'nav',
        title: 'Dashboard',
        subtitle: 'Přejít na přehled',
        url: '/dashboard',
      },
      {
        id: 'nav-areas',
        type: 'nav',
        title: 'Moje záhony',
        subtitle: 'Seznam záhonů',
        url: '/growing-areas',
      },
      {
        id: 'nav-library',
        type: 'nav',
        title: 'Knihovna',
        subtitle: 'Plant-O-Pedia',
        url: '/plant-o-pedia',
      },
      {
        id: 'act-new-area',
        type: 'action',
        title: 'Nový záhon',
        subtitle: 'Vytvořit novou pěstební plochu',
        url: '/growing-areas?action=new',
      },
      {
        id: 'act-new-batch',
        type: 'action',
        title: 'Nová výsadba',
        subtitle: 'Založit nový pěstební cyklus',
        url: '/growth/batches?action=new',
      },
    ];

    staticActions
      .filter((a) => a.title.toLowerCase().includes(trimmedQuery))
      .forEach((a) => unified.push(a));

    // Sort by similarity if present, otherwise type priority
    return unified.sort((a, b) => {
      if (a.similarity !== undefined && b.similarity !== undefined) {
        return b.similarity - a.similarity;
      }
      return 0; // Maintain order from aggregation
    });
  }, [trimmedQuery, remoteResults, areas, batches, tasks]);

  return {
    results,
    isLoading: isRemoteLoading && trimmedQuery.length >= 2,
    hasQuery: trimmedQuery.length >= 2,
  };
};
